import React, { useState, useEffect, useCallback } from 'react';
import { coachesAPI, sessionsAPI } from '../services/api';
import { UserIcon, ClockIcon } from './Icons';
import styles from '../styles/SchedulingScreen.module.css';

/**
 * SchedulingScreen Component
 * Displays available time slots for a selected coach and allows booking
 *
 * Props:
 * - coach: Coach object with id, displayName, bio, specializations, sessionDuration, profileImage
 * - onBack: Callback to go back to previous screen
 * - onBookingComplete: Callback when session is successfully booked
 */
function SchedulingScreen({ coach, onBack, onBookingComplete }) {
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Fetch available slots on mount
  useEffect(() => {
    const fetchSlots = async () => {
      if (!coach?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await coachesAPI.getCoachSlots(coach.id);
        const slotStrings = response.slots; // Keep as ISO strings

        console.log('[SchedulingScreen] Received slots from backend:');
        console.log('  First 3 slots:', slotStrings.slice(0, 3));
        console.log('  Slot type:', typeof slotStrings[0]);

        setSlots(slotStrings);

        // Auto-select first available date
        if (slotStrings.length > 0) {
          setSelectedDate(getDateKey(slotStrings[0]));
        }
      } catch (err) {
        console.error('[SchedulingScreen] Failed to fetch slots:', err);
        setError('Unable to load available times. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSlots();
  }, [coach?.id]);

  // Helper to get date key (YYYY-MM-DD) from ISO string
  const getDateKey = (isoString) => {
    return isoString.split('T')[0];
  };

  // Group slots by date
  const slotsByDate = useCallback(() => {
    const grouped = {};
    slots.forEach(isoString => {
      const dateKey = getDateKey(isoString);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(isoString); // Store ISO string
    });
    return grouped;
  }, [slots]);

  // Get available dates
  const availableDates = Object.keys(slotsByDate()).sort();

  // Format date for display
  const formatDate = (dateKey) => {
    const date = new Date(dateKey + 'T00:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayKey = today.toISOString().split('T')[0];
    const tomorrowKey = tomorrow.toISOString().split('T')[0];

    if (dateKey === todayKey) {
      return 'Today';
    } else if (dateKey === tomorrowKey) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Format time for display
  const formatTime = (isoString) => {
    const date = new Date(isoString); // Convert only for display
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle booking
  const handleBook = async () => {
    if (!selectedSlot) return;

    setIsBooking(true);
    setError(null);

    try {
      // selectedSlot is now an ISO string, not a Date
      console.log('[SchedulingScreen] Booking slot:', selectedSlot);
      console.log('[SchedulingScreen] Slot type:', typeof selectedSlot);
      const response = await sessionsAPI.bookSession(coach.id, selectedSlot);
      const session = response?.session;

      if (onBookingComplete && session) {
        onBookingComplete(session);
      }
    } catch (err) {
      console.error('[SchedulingScreen] Failed to book session:', err);
      setError(err.message || 'Failed to book session. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.schedulingScreen}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBack}>
            ← Back
          </button>
          <h2>Loading available times...</h2>
        </div>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Finding available time slots...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.schedulingScreen}>
      {/* Header with coach info */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
        <h2>Schedule a Session</h2>
      </div>

      {/* Coach summary */}
      <div className={styles.coachSummary}>
        <div className={styles.coachImage}>
          {coach.profileImage ? (
            <img src={coach.profileImage} alt={coach.displayName} />
          ) : (
            <div className={styles.coachImageFallback}>
              <UserIcon size={32} />
            </div>
          )}
        </div>
        <div className={styles.coachInfo}>
          <div className={styles.coachName}>{coach.displayName}</div>
          <div className={styles.sessionDuration}>
            <ClockIcon size={14} />
            <span>{coach.sessionDuration || 30} minute session</span>
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {slots.length === 0 && !error ? (
        <div className={styles.noSlots}>
          <p>No available times in the next 2 weeks.</p>
          <p>Please check back later or try a different coach.</p>
        </div>
      ) : (
        <>
          {/* Date selector */}
          <div className={styles.dateSelector}>
            {availableDates.map(dateKey => (
              <button
                key={dateKey}
                className={`${styles.dateButton} ${selectedDate === dateKey ? styles.selected : ''}`}
                onClick={() => setSelectedDate(dateKey)}
              >
                {formatDate(dateKey)}
              </button>
            ))}
          </div>

          {/* Time slots for selected date */}
          {selectedDate && slotsByDate()[selectedDate] && (
            <div className={styles.timeSlots}>
              <div className={styles.timeSlotsHeader}>
                <h3>Available Times</h3>
                <p className={styles.timezoneNote}>
                  All times shown in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </p>
              </div>
              <div className={styles.slotsGrid}>
                {slotsByDate()[selectedDate].map((isoString, index) => (
                  <button
                    key={index}
                    className={`${styles.slotButton} ${selectedSlot === isoString ? styles.selected : ''}`}
                    onClick={() => setSelectedSlot(isoString)}
                  >
                    {formatTime(isoString)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Book button */}
          {selectedSlot && (
            <div className={styles.bookingSection}>
              <div className={styles.selectedTime}>
                <strong>Selected:</strong> {formatDate(getDateKey(selectedSlot))} at {formatTime(selectedSlot)}
              </div>
              <button
                className={styles.bookButton}
                onClick={handleBook}
                disabled={isBooking}
              >
                {isBooking ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SchedulingScreen;
